// src/components/Charts.js
import React, { useMemo, useState, useEffect, useId } from "react";
import styled from "styled-components";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  LineChart,
  Line,
  Cell,
  LabelList,
} from "recharts";

// FORCE DISABLE ALL ANIMATION DURING PRINT
const NO_ANIM = {
  isAnimationActive: false,
  animationDuration: 0,
  animationBegin: 0,
};

// PRINT DIMENSIONS - Fixed pixel values for reliable print rendering
const PRINT_WIDTH = 700;
const PRINT_HEIGHT = 320;

// ================================
// FIXED BAR SIZE CONSTANTS
// ================================

// Vertical bars (Metric Tons Over Time)
const BAR_SIZE_VERTICAL_PRINT = 30;
const BAR_SIZE_VERTICAL_SCREEN = 95;

// Horizontal bars (buyers, sellers, countries, ports, HS codes)
const BAR_SIZE_HORIZONTAL_PRINT = 12;
const BAR_SIZE_HORIZONTAL_SCREEN = 28;


// Styled Components
const ChartCard = styled.div`
  background-color: ${({ isLightMode }) =>
    isLightMode ? "#FFFFFF" : "#2D354A"};
  padding: 25px;
  border-radius: 16px;
  margin-bottom: 35px;
  page-break-inside: avoid;
  position: relative;
  border: 0 !important;

  &.print-mode-chart {
    background-color: #FFFFFF !important;
    background: #FFFFFF !important;
  }

  @media print {
    background-color: #FFFFFF !important;
    padding: 10px !important;
    border-radius: 8px !important;
    margin-bottom: 0 !important;
    margin-top: 0 !important;
    page-break-inside: avoid !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`;

// Helpers
const formatCurrency = (num) => {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(1)}`;
};

const formatTons = (n) => {
  const num = Number(n) || 0;
  if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M MT`;
  if (num >= 1e3) return `${Math.round(num / 1e3)}K MT`;
  return `${Math.round(num)} MT`;
};

const formatCategoryXAxisPrint = (value) => {
  const num = Number(value) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M MT`;
  }

  if (num >= 1_000) {
    return `${Math.round(num / 1_000)}K MT`;
  }

  return `${Math.round(num)} MT`;
};


// ON-SCREEN ONLY: bar label formatter for horizontal charts
const formatBarLabelMT = (value) => {
  const num = Number(value) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M MT`; // keep 1 decimal
  }

  if (num >= 1_000) {
    return `${Math.round(num / 1_000)}K MT`; // NO decimals
  }

  return `${Math.round(num)} MT`;
};


// ON-SCREEN ONLY: Axis formatter for horizontal bars
const formatAxisMT = (value) => {
  const num = Number(value) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M MT`; // keep 1 decimal
  }

  if (num >= 1_000) {
    return `${Math.round(num / 1_000)}K MT`; // NO decimals
  }

  return `${Math.round(num)} MT`;
};


const formatTonsNoDecimalK = (n) => {
  const num = Number(n) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M MT`;
  }

  if (num >= 1_000) {
    return `${Math.round(num / 1_000)}K MT`;
  }

  return `${Math.round(num)} MT`;
};


const formatYAxisMT_Print = (n) => {
  const num = Number(n) || 0;
  if (num >= 1000) return `${Math.round(num / 1000)}K MT`;
  return `${Math.round(num)} MT`;
};

// CATEGORY DISTRIBUTION — label formatter (no decimals for K)
const formatCategoryMT = (n) => {
  const num = Number(n) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M MT`;
  }

  if (num >= 1_000) {
    return `${Math.round(num / 1_000)}K MT`;
  }

  return `${Math.round(num)} MT`;
};


// PRINT ONLY — bar charts (buyers, sellers, ports, etc)
const formatPrintMT = (n) => {
  const num = Number(n) || 0;

  if (num < 1000) {
    return `${Math.round(num)} MT`;
  }

  return `${Math.round(num / 1000)}K MT`;
};

// PRINT ONLY — Metric Tons Over Time
const formatPrintMT_TimeSeries = (n) => {
  const num = Number(n) || 0;

  if (num < 1000) {
    return `${Math.round(num)}`;
  }

  return `${Math.round(num / 1000)}K`;
};






const formatValPerKg = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const formatMonthLabel = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(Number(y), Number(m) - 1);
  return d.toLocaleString("default", { month: "short", year: "numeric" });
};

// Category Mapping
const categoryLabels = {
  "ALUM-PR": "Aluminum Wire Imports",
  ALBW: "Aluminum Building Wire Imports",
  PV: "Photovoltaic Wire Imports",
  OHT: "Overhead Transmission Wire Imports",
  "COPP-PR": "Copper Wire (Price Inferred) Imports",
  COPP: "Copper Wire Imports",
  CORD: "Cord Imports",
  RESI: "Residential Imports",
  MV: "Medium Voltage Imports",
  TRAY: "Tray Cable Imports",
  SPEC: "Specialty Item Imports",
  COMMS: "Communications Wire Imports",
  TOOL: "Tool Imports",
  RM: "Raw Material Imports",
  UNMP: "Unmapped Imports",
  CATH: "Copper Cathode",
};

// Label Formatters
const formatCountry = (text) =>
  text.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const formatPort = (text) => {
  const [city, state] = text.split(",");
  if (!state) return formatCountry(text);
  const cityProper = city
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return `${cityProper}, ${state.trim().toUpperCase()}`;
};

function Charts({
  data,
  chartType,
  isLightMode,
  isPrint = false,
  forceRender = false,
  monthRange = 12,
}) {

  // Generate a unique ID for this component instance to prevent gradient collisions
  const uniqueId = useId();
  
  useEffect(() => {
    const handleBeforePrint = () => {
      window.dispatchEvent(new Event("resize"));
    };
    window.addEventListener("beforeprint", handleBeforePrint);
    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
    };
  }, []);

  // PRINT MODE DETECTION
  const isPrinting =
    forceRender ||
    isPrint ||
    (typeof window !== "undefined" && window.matchMedia("print").matches);

  // When printing, always use light mode styling
  const effectiveLightMode = isPrinting ? true : isLightMode;
  const textColor = effectiveLightMode ? "#191B27" : "#DCE4F2";

  const visible = {
    TotalValue: false,
    MetricTons: true,
    ValPerKg: false,
  };

  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data)) return {};

    const sumByKey = (arr, key, labelKey, limit = 10) => {
      const stats = {};
      arr.forEach((row) => {
        const label = String(row[labelKey] || "").trim();
        const value = Number(row[key]) || 0;
        if (label && label !== "Unknown" && label !== "Not Declared") {
          stats[label] = (stats[label] || 0) + value;
        }
      });
      return {
        data: Object.entries(stats)
          .sort((a, b) => b[1] - a[1])
          .slice(0, limit)
          .map(([name, value]) => ({ name, value })),
      };
    };

    const categoryTotals = {};
    data.forEach((row) => {
      const label = String(row["Category"] || "").trim();
      const tons = Number(row["Metric Tons"]) || 0;
      if (label) categoryTotals[label] = (categoryTotals[label] || 0) + tons;
    });

    let fullCategoryData = Object.keys(categoryLabels).map((abbr) => ({
      name: abbr,
      value: categoryTotals[abbr] || 0,
    }));
    fullCategoryData.sort((a, b) => b.value - a.value);

    const monthly = {};
    data.forEach((row) => {
      if (!row.Date) return;
      const d = new Date(row.Date);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!monthly[key])
        monthly[key] = { TotalValue: 0, MetricTons: 0, ValPerKg: 0, c: 0 };
      monthly[key].TotalValue += Number(row.totalValue) || 0;
      monthly[key].MetricTons += Number(row["Metric Tons"]) || 0;
      monthly[key].ValPerKg += Number(row.valPerKg) || 0;
      monthly[key].c += 1;
    });

    const timeSeries = Object.entries(monthly)
      .map(([month, v]) => ({
        month,
        TotalValue: v.TotalValue,
        MetricTons: v.MetricTons,
        ValPerKg: v.c ? v.ValPerKg / v.c : 0,
      }))
      .sort((a, b) => new Date(a.month) - new Date(b.month));

    return {
      buyers: sumByKey(data, "Metric Tons", "buyer"),
      sellers: sumByKey(data, "Metric Tons", "seller"),
      countries: sumByKey(data, "Metric Tons", "Country of Origin"),
      ports: sumByKey(data, "Metric Tons", "Port of Arrival"),
      hsCodes: sumByKey(data, "Metric Tons", "HS Code"),
      categories: { data: fullCategoryData },
      timeSeries,
    };
  }, [data]);
  
  // ============================================
  // RENDER BAR CHART (Horizontal bars)
  // ============================================
  const renderBarChart = (block, title) => {
    // Unique gradient ID using React's useId + title
    const gradientId = `grad-${uniqueId}-${title.replace(/\s+/g, "-")}`;

    const renderChartContent = (fontSize = { title: 30, axis: 18, label: 15, yAxis: 17 }) => (
      <>
        <text
          x="50%"
          y={isPrinting ? 25 : 50}
          textAnchor="middle"
          fontFamily="Montserrat, sans-serif"
          fontSize={isPrinting ? 18 : fontSize.title}
          fontWeight="700"
          fill={textColor}
        >
          {title}
        </text>

        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0093FF" />
            <stop offset="100%" stopColor="#00D160" />
          </linearGradient>
        </defs>

        <XAxis
          type="number"
          tickFormatter={formatTonsNoDecimalK}
          tick={{
            fill: textColor,
            fontSize: isPrinting ? 10 : fontSize.axis,
            fontWeight: 600,
            fontFamily: "Montserrat, sans-serif",
          }}
          tickMargin={4}
        />

        <YAxis
          type="category"
          dataKey="name"
          width={isPrinting ? 140 : 280}
          interval={0}
          tick={(props) => {
            const { x, y, payload } = props;
            let label = payload.value;
            if (chartType === "categories") {
              label = categoryLabels[payload.value] || payload.value;
            } else if (title.toLowerCase().includes("country")) {
              label = formatCountry(payload.value);
            } else if (title.toLowerCase().includes("ports")) {
              label = formatPort(payload.value);
            }
            return (
              <text
                x={x - 5}
                y={y + 4}
                fill={textColor}
                fontSize={isPrinting ? 9 : fontSize.yAxis}
                fontFamily="Montserrat"
                textAnchor="end"
                fontWeight={payload.index === 0 ? "700" : "400"}
                style={{ whiteSpace: "nowrap" }}
              >
                {label}
              </text>
            );
          }}
          tickMargin={8}
        />

        <Bar
  dataKey="value"
  barSize={
    isPrinting
      ? BAR_SIZE_HORIZONTAL_PRINT
      : BAR_SIZE_HORIZONTAL_SCREEN
  }
  radius={[0, 6, 6, 0]}
>

          {block.data.map((_, i) => (
            <Cell
              key={i}
              fill={i === 0 ? "#00D160" : `url(#${gradientId})`}
            />
          ))}
          <LabelList
            {...NO_ANIM}
            dataKey="value"
            position="right"
formatter={(val) =>
  isPrinting
    ? formatPrintMT(val)
    : formatBarLabelMT(val)
}

            style={{
              fill: textColor,
              fontSize: isPrinting ? 8 : fontSize.label,
              fontFamily: "Montserrat",
              fontWeight: 500,
            }}
          />
        </Bar>
      </>
    );

    return (
      <ChartCard isLightMode={effectiveLightMode} className={isPrinting ? "print-mode-chart" : ""}>
        <div className="chart-print-wrapper">
          {isPrinting ? (
            <BarChart
              width={PRINT_WIDTH}
              height={PRINT_HEIGHT}
              data={block.data}
              layout="vertical"
              margin={{ top: 35, right: 60, left: 20, bottom: 15 }}
              barCategoryGap="15%"
              isAnimationActive={false}
            >
              {renderChartContent()}
            </BarChart>
          ) : (
            <ResponsiveContainer width="100%" height={770}>
              <BarChart
                data={block.data}
                layout="vertical"
                margin={{ top: 80, right: 80, left: 80, bottom: 30 }}
                barCategoryGap="20%"
                isAnimationActive={false}
              >
                {renderChartContent()}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>
    );
  };

  // ============================================
  // RENDER METRIC TONS OVER TIME (Vertical bars)
  // ============================================
const renderMetricTonsOverTimeBarChart = () => {
    const gradientId = `grad-${uniqueId}-metric-tons-time`;

const recentData = chartData.timeSeries;

    const maxMetricTons = Math.max(
      ...recentData.map(d => d.MetricTons)
    );

    const maxIndex = recentData.findIndex(
      d => d.MetricTons === maxMetricTons
    );


    const renderChartContent = () => (
      <>
        <text
          x="50%"
          y={isPrinting ? 20 : 40}
          textAnchor="middle"
          fontFamily="Montserrat, sans-serif"
          fontSize={isPrinting ? 16 : 30}
          fontWeight="700"
          fill={textColor}
        >
          Metric Tons Over Time
        </text>

        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0093FF" />
            <stop offset="100%" stopColor="#00D160" />
          </linearGradient>
        </defs>

      <XAxis
  dataKey="month"
  tickFormatter={formatMonthLabel}
  tick={{
    fill: textColor,
    fontSize: isPrinting ? 8 : 16,
    fontWeight: 600,
    fontFamily: "Montserrat",
  }}
  tickMargin={isPrinting ? 4 : 12}
  interval={recentData.length > 13 ? 2 : 0}
/>

        <YAxis
  tickFormatter={(val) => {
    if (!isPrinting) return formatTons(val);

    const num = Number(val) || 0;
    if (num >= 1000) return `${Math.round(num / 1000)}K`;
    return `${Math.round(num)}`;
  }}
  tick={{
    fill: textColor,
    fontSize: isPrinting ? 9 : 16,
    fontWeight: 600,
    fontFamily: "Montserrat",
  }}
  width={isPrinting ? 50 : 80}
/>


 <Bar
  dataKey="MetricTons"
  barSize={
    isPrinting
      ? BAR_SIZE_VERTICAL_PRINT
      : BAR_SIZE_VERTICAL_SCREEN
  }
  radius={[6, 6, 0, 0]}
>

  {recentData.map((entry, index) => (
    <Cell
      key={`cell-${index}`}
      fill={
        index === maxIndex
          ? "#CD7F32"
          : `url(#${gradientId})`
      }
    />
  ))}

  {!isPrinting && (
    <LabelList
      {...NO_ANIM}
      dataKey="MetricTons"
      position="top"
      formatter={(val) => formatTons(val)}
      style={{
        fill: textColor,
        fontSize: 13,
        fontFamily: "Montserrat",
        fontWeight: 600,
      }}
    />
  )}

{isPrinting && (
  <LabelList
    dataKey="MetricTons"
    content={({ x, y, width, value }) => {
      if (value <= 0) return null;

      return (
        <text
          x={x + width / 2}
          y={y - 6}              // ⬅️ keeps label inside SVG
          textAnchor="middle"
          fill={textColor}
          fontSize={9}
          fontFamily="Montserrat"
          fontWeight={600}
        >
          {formatPrintMT_TimeSeries(value)}
        </text>
      );
    }}
  />
)}



</Bar>


      </>
    );

    return (
      <ChartCard isLightMode={effectiveLightMode} className={isPrinting ? "print-mode-chart" : ""}>
        <div className="chart-print-wrapper">
          {isPrinting ? (
            <BarChart
              width={PRINT_WIDTH}
              height={PRINT_HEIGHT}
              data={recentData}
              margin={{ top: 35, right: 20, left: 10, bottom: 15 }}
              barSize={isPrinting ? 20 : 95}
              barGap={2}
              isAnimationActive={false}
            >
              {renderChartContent()}
            </BarChart>
          ) : (
            <ResponsiveContainer width="100%" height={770}>
              <BarChart
                data={recentData}
                margin={{ top: 80, right: 60, left: 60, bottom: 30 }}
                barSize={95}
                barGap={2}
                isAnimationActive={false}
              >
                {renderChartContent()}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>
    );
  };

  // ============================================
  // RENDER CATEGORY DISTRIBUTION
  // ============================================
  const renderCategoryDistribution = () => {
    const ALUMINUM = ["ALUM-PR", "ALBW", "OHT", "PV"];
    const COPPER = [
      "COMMS",
      "CATH",
      "COPP-PR",
      "COPP",
      "CORD",
      "MV",
      "RESI",
      "SPEC",
      "TRAY",
    ];
    const OTHER = ["RM", "TOOL", "UNMP"];

    const allCategories = chartData.categories?.data || [];
    const totals = {};
    allCategories.forEach((item) => {
      totals[item.name] = item.value;
    });

    const makeRows = (arr) =>
      arr
        .filter((abbr) => totals[abbr] !== undefined)
        .map((abbr) => ({
          name: abbr,
          label: categoryLabels[abbr] || abbr,
          value: totals[abbr] || 0,
          color: "gradient",
        }));

    const cleanRows = [
      ...makeRows(ALUMINUM),
      {
        name: "TOTAL_ALUMINUM",
        label: "Total Aluminum Imports",
        value: ALUMINUM.reduce((s, x) => s + (totals[x] || 0), 0),
        color: "#B0B0B0",
      },
      ...makeRows(COPPER),
      {
        name: "TOTAL_COPPER",
        label: "Total Copper Imports",
        value: COPPER.reduce((s, x) => s + (totals[x] || 0), 0),
        color: "#CD7F32",
      },
      ...makeRows(OTHER),
      {
        name: "TOTAL_ALL",
        label: "Total Imports",
        value: [...ALUMINUM, ...COPPER, ...OTHER].reduce(
          (s, x) => s + (totals[x] || 0),
          0
        ),
        color: "#002B5B",
      },
    ];

    const gradientId = `grad-${uniqueId}-category-dist`;

    const renderChartContent = () => (
      <>
        <text
          x="50%"
          y={isPrinting ? 20 : 50}
          textAnchor="middle"
          fontFamily="Montserrat, sans-serif"
          fontSize={isPrinting ? 16 : 30}
          fontWeight="700"
          fill={textColor}
        >
          Category Distribution
        </text>

        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#0093FF" />
            <stop offset="100%" stopColor="#00D160" />
          </linearGradient>
        </defs>

        <XAxis
          type="number"
           tickFormatter={(val) =>
   isPrinting ? formatCategoryXAxisPrint(val) : formatTonsNoDecimalK(val)
}
          tick={{
            fill: textColor,
            fontSize: isPrinting ? 9 : 16,
            fontWeight: 600,
            fontFamily: "Montserrat, sans-serif",
          }}
        />

        <YAxis
          type="category"
          dataKey="label"
          width={isPrinting ? 160 : 280}
          interval={0}
          tick={({ x, y, payload }) => {
            const label = payload.value;
            const boldLabels = [
              "Total Aluminum Imports",
              "Total Copper Imports",
              "Total Imports",
            ];
            const isBold = boldLabels.includes(label);
            return (
              <text
                x={x - 5}
                y={y + 3}
                fill={textColor}
                fontSize={isPrinting ? 8 : 17}
                fontFamily="Montserrat, sans-serif"
                fontWeight={isBold ? 700 : 400}
                textAnchor="end"
              >
                {label}
              </text>
            );
          }}
        />

        <Bar dataKey="value" radius={[0, 6, 6, 0]}>
          {cleanRows.map((row, i) => (
            <Cell
              key={i}
              fill={row.color === "gradient" ? `url(#${gradientId})` : row.color}
              stroke="none"
            />
          ))}
         {/* ON-SCREEN DASHBOARD: keep all labels */}
{!isPrinting && (
  <LabelList
    {...NO_ANIM}
    dataKey="value"
    position="right"
    formatter={(v) => formatCategoryMT(v)}
    style={{
      fill: textColor,
      fontSize: 15,
      fontFamily: "Montserrat, sans-serif",
      fontWeight: 500,
    }}
  />
)}

{/* PRINT MODE: only show TOTAL labels */}
{isPrinting && (
  <LabelList
  {...NO_ANIM}
  dataKey="value"
  content={({ x, y, width, index, value }) => {
    const label = cleanRows[index]?.label;

    const allowed = [
      "Total Aluminum Imports",
      "Total Copper Imports",
      "Total Imports",
    ];

    if (!allowed.includes(label)) return null;

    return (
      <text
        x={x + width + 6}   // ✅ end of bar
        y={y + 4}
        fill={textColor}
        fontSize={10}
        fontFamily="Montserrat, sans-serif"
        fontWeight={700}     // ✅ bold
        textAnchor="start"
      >
        {(() => {
  const num = Number(value) || 0;

  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M MT`;
  }

  if (num >= 1_000) {
    return `${Math.round(num / 1_000)}K MT`;
  }

  return `${Math.round(num)} MT`;
})()}

      </text>
    );
  }}
/>

)}

        </Bar>
      </>
    );

    return (
      <ChartCard isLightMode={effectiveLightMode} className={isPrinting ? "print-mode-chart" : ""}>
        <div className="chart-print-wrapper">
          {isPrinting ? (
            <BarChart
              width={PRINT_WIDTH}
              height={PRINT_HEIGHT}
              data={cleanRows}
              layout="vertical"
              margin={{ top: 50, right: 70, left: 10, bottom: 15 }}
              barSize={BAR_SIZE_HORIZONTAL_PRINT}
              barGap={2}
              isAnimationActive={false}
            >
              {renderChartContent()}
            </BarChart>
          ) : (
            <ResponsiveContainer width="100%" height={770}>
              <BarChart
                data={cleanRows}
                layout="vertical"
                margin={{ top: 100, right: 60, left: 60, bottom: 40 }}
                barSize={BAR_SIZE_HORIZONTAL_SCREEN}
                barGap={6}
                isAnimationActive={false}
              >
                {renderChartContent()}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>
    );
  };

  // ============================================
  // RENDER TIME SERIES (Line Chart)
  // ============================================
 
const renderTimeSeries = () => {
const lineYAxisColor = isPrinting
  ? "#4B5563"
  : isLightMode
  ? "#191B27"
  : "#FFFFFF";
    const renderChartContent = () => (
      <>
        <text
          x="50%"
          y={isPrinting ? 20 : 35}
          textAnchor="middle"
          fontFamily="Montserrat, sans-serif"
          fontSize={isPrinting ? 16 : 30}
          fontWeight="700"
          fill={textColor}
        >
          Imports Over Time
        </text>

        <XAxis
          dataKey="month"
          tickFormatter={formatMonthLabel}
          tick={{
            fill: textColor,
            fontSize: isPrinting ? 8 : 18,
            fontWeight: 700,
            fontFamily: "Montserrat, sans-serif",
          }}
          tickMargin={isPrinting ? 4 : 8}
          interval={
  isPrinting
    ? 2
    : chartData.timeSeries.length > 13
    ? 2
    : 0
}

        />

        {visible.TotalValue && (
          <YAxis
            yAxisId="left"
            width={isPrinting ? 50 : 80}
            tickFormatter={formatCurrency}
            tick={{ fill: "#0093FF", fontSize: isPrinting ? 8 : 12 }}
          />
        )}

      {visible.MetricTons && (
  <YAxis
    yAxisId="right"
    orientation="right"
    width={isPrinting ? 45 : 70}
    tickFormatter={(val) => {
      const n = Number(val) || 0;
      if (n === 0) return "0 MT";
      if (n >= 1000) return `${Math.round(n / 1000)}K\u00A0MT`;
      return `${Math.round(n)}\u00A0MT`;
    }}
    tick={{
      fill: lineYAxisColor,
      fontSize: isPrinting ? 8 : 12,
      fontFamily: "Montserrat",
      fontWeight: 600,
    }}
  />
)}


        )}

        {visible.ValPerKg && (
          <YAxis
            yAxisId="valkg"
            orientation="right"
            width={isPrinting ? 45 : 70}
            tickFormatter={formatValPerKg}
            tick={{ fill: "#8A00C2", fontSize: isPrinting ? 8 : 12 }}
            dx={35}
            axisLine={false}
          />
        )}

        <Line
          isAnimationActive={false}
          yAxisId="left"
          type="monotone"
          dataKey="TotalValue"
          stroke="#0093FF"
          strokeWidth={isPrinting ? 2 : 3}
          dot={false}
          name="Total Value"
          strokeOpacity={visible.TotalValue ? 1 : 0}
        >
          <LabelList
            {...NO_ANIM}
            valueAccessor={(entry) => entry.TotalValue}
            position="top"
            formatter={(val) => formatCurrency(val)}
            style={{ fontSize: isPrinting ? 6 : 13, fill: isPrinting
  ? "#4B5563"
  : isLightMode
  ? "#191B27"
  : "#FFFFFF"
 }}
          />
        </Line>

       <Line
  isAnimationActive={false}
  yAxisId="right"
  type="monotone"
  dataKey="MetricTons"
  stroke="#0093FF"
  strokeWidth={isPrinting ? 2 : 3}
  strokeLinecap="round"
  strokeLinejoin="round"
  dot={false}
  name="Metric Tons"
  strokeOpacity={visible.MetricTons ? 1 : 0}
>

          <LabelList
            {...NO_ANIM}
            valueAccessor={(entry) => entry.MetricTons}
            position="top"
            formatter={(val) => formatTons(val)}
            style={{ fontSize: isPrinting ? 6 : 13, fill: textColor }}
          />
        </Line>

        <Line
          isAnimationActive={false}
          yAxisId="valkg"
          type="monotone"
          dataKey="ValPerKg"
          stroke="#8A00C2"
          strokeWidth={isPrinting ? 2 : 3}
          dot={false}
          name="Val/KG ($)"
          strokeOpacity={visible.ValPerKg ? 1 : 0}
        >
          <LabelList
            {...NO_ANIM}
            valueAccessor={(entry) => entry.ValPerKg}
            position="top"
            formatter={(val) => formatValPerKg(val)}
            style={{ fontSize: isPrinting ? 6 : 13, fill: textColor }}
          />
        </Line>
      </>
    );

    return (
      <ChartCard isLightMode={effectiveLightMode} className={isPrinting ? "print-mode-chart" : ""}>
        <div className="chart-print-wrapper">
          {isPrinting ? (
            <LineChart
              width={PRINT_WIDTH}
              height={PRINT_HEIGHT}
              data={chartData.timeSeries}
              margin={{ top: 35, right: 30, left: 20, bottom: 15 }}
              isAnimationActive={false}
            >
              {renderChartContent()}
            </LineChart>
          ) : (
            <ResponsiveContainer width="100%" height={770}>
              <LineChart
                data={chartData.timeSeries}
                margin={{ top: 80, right: 60, left: 60, bottom: 30 }}
                isAnimationActive={false}
              >
                {renderChartContent()}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>
    );
  };

  // ============================================
  // SWITCH
  // ============================================
  switch (chartType) {
    case "buyers":
      return renderBarChart(chartData.buyers, "Top Domestic Importers");
    case "sellers":
      return renderBarChart(chartData.sellers, "Top International Suppliers");
    case "countries":
      return renderBarChart(chartData.countries, "Country Distribution");
    case "metricTonsOverTime":
      return renderMetricTonsOverTimeBarChart();
    case "ports":
      return renderBarChart(chartData.ports, "Ports of Arrival");
    case "hsCodes":
      return renderBarChart(chartData.hsCodes, "Top HS Codes");
    case "categories":
      return renderCategoryDistribution();
    case "timeSeries":
      return renderTimeSeries();
    default:
      return null;
  }
}

export default Charts;